import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Calendar, Users, CreditCard, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Booking = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [suites, setSuites] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [formData, setFormData] = useState({
    suite_id: location.state?.selectedSuite || '',
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    check_in: null,
    check_out: null,
    num_guests: 1,
    special_requests: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${API}/suites`)
      .then((res) => res.json())
      .then((data) => {
        setSuites(data);
        if (!formData.suite_id && data.length > 0) {
          setFormData((prev) => ({ ...prev, suite_id: data[0].id }));
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching suites:', err);
        setLoading(false);
      });
  }, []);

  const selectedSuite = suites.find((s) => s.id === formData.suite_id);

  const calculateTotalAmount = () => {
    if (!selectedSuite || !formData.check_in || !formData.check_out) return 0;
    
    const diffTime = Math.abs(formData.check_out - formData.check_in);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return selectedSuite.price_per_night * diffDays;
  };

  const calculateNumNights = () => {
    if (!formData.check_in || !formData.check_out) return 0;
    
    const diffTime = Math.abs(formData.check_out - formData.check_in);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.guest_name.trim()) {
      newErrors.guest_name = 'Name is required';
    }
    if (!formData.guest_email.trim()) {
      newErrors.guest_email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.guest_email)) {
      newErrors.guest_email = 'Email is invalid';
    }
    if (!formData.guest_phone.trim()) {
      newErrors.guest_phone = 'Phone is required';
    }
    if (!formData.check_in) {
      newErrors.check_in = 'Check-in date is required';
    }
    if (!formData.check_out) {
      newErrors.check_out = 'Check-out date is required';
    }
    if (formData.check_in && formData.check_out && formData.check_out <= formData.check_in) {
      newErrors.check_out = 'Check-out must be after check-in';
    }
    if (!formData.suite_id) {
      newErrors.suite_id = 'Please select a suite';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Create booking
      const bookingResponse = await fetch(`${API}/bookings/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          suite_id: formData.suite_id,
          guest_name: formData.guest_name,
          guest_email: formData.guest_email,
          guest_phone: formData.guest_phone,
          check_in: formData.check_in.toISOString(),
          check_out: formData.check_out.toISOString(),
          num_guests: parseInt(formData.num_guests),
          special_requests: formData.special_requests || null
        })
      });

      if (!bookingResponse.ok) {
        throw new Error('Failed to create booking');
      }

      const booking = await bookingResponse.json();

      // Initialize payment
      const callbackUrl = `${window.location.origin}/payment/callback`;
      const paymentResponse = await fetch(`${API}/bookings/payment/initialize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          booking_id: booking.id,
          callback_url: callbackUrl
        })
      });

      if (!paymentResponse.ok) {
        throw new Error('Failed to initialize payment');
      }

      const paymentData = await paymentResponse.json();

      // Redirect to Paystack
      if (paymentData.authorization_url) {
        window.location.href = paymentData.authorization_url;
      }
    } catch (error) {
      console.error('Error processing booking:', error);
      setErrors({ general: 'Failed to process booking. Please try again or contact us directly.' });
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="booking-loading" className="min-h-screen flex items-center justify-center pt-24">
        <p className="text-[#3A3A3A]">Loading...</p>
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="min-h-screen bg-[#FAF8F5] pt-24">
      {/* Header */}
      <section className="py-16 px-6 md:px-12 lg:px-24 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          data-testid="booking-page-title"
          className="font-serif font-bold tracking-tighter leading-none text-[#1E3A8A] mb-6"
          style={{ fontSize: '26px' }}
        >
          Reserve Your Suite
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          data-testid="booking-page-subtitle"
          className="text-base leading-relaxed text-[#3A3A3A] max-w-3xl mx-auto"
        >
          Complete your booking in just a few steps. Secure payment via Paystack.
        </motion.p>
      </section>

      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Booking Form */}
            <div className="lg:col-span-2">
              <motion.form
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                onSubmit={handleSubmit}
                data-testid="booking-form"
                className="bg-white border border-[#E5E5E5] p-8"
              >
                <h2 className="font-serif text-2xl sm:text-3xl tracking-tight font-light text-[#1A1A1A] mb-8">
                  Booking Details
                </h2>

                {errors.general && (
                  <div data-testid="booking-error-general" className="mb-6 p-4 bg-red-50 border border-red-200 text-red-800 flex items-start gap-2">
                    <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                    <span>{errors.general}</span>
                  </div>
                )}

                <div className="space-y-6">
                  {/* Suite Selection */}
                  <div>
                    <label htmlFor="suite_id" className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">
                      Select Suite *
                    </label>
                    <select
                      id="suite_id"
                      name="suite_id"
                      data-testid="booking-suite-select"
                      value={formData.suite_id}
                      onChange={handleChange}
                      className="w-full border border-[#E5E5E5] px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#C9A84C] transition-colors"
                    >
                      {suites.map((suite) => (
                        <option key={suite.id} value={suite.id}>
                          {suite.name} - ₦{suite.price_per_night.toLocaleString()}/night
                        </option>
                      ))}
                    </select>
                    {errors.suite_id && <p className="text-red-600 text-sm mt-1">{errors.suite_id}</p>}
                  </div>

                  {/* Guest Name */}
                  <div>
                    <label htmlFor="guest_name" className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="guest_name"
                      name="guest_name"
                      data-testid="booking-name-input"
                      value={formData.guest_name}
                      onChange={handleChange}
                      className="w-full border border-[#E5E5E5] px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#C9A84C] transition-colors"
                    />
                    {errors.guest_name && <p className="text-red-600 text-sm mt-1">{errors.guest_name}</p>}
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="guest_email" className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="guest_email"
                      name="guest_email"
                      data-testid="booking-email-input"
                      value={formData.guest_email}
                      onChange={handleChange}
                      className="w-full border border-[#E5E5E5] px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#C9A84C] transition-colors"
                    />
                    {errors.guest_email && <p className="text-red-600 text-sm mt-1">{errors.guest_email}</p>}
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="guest_phone" className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      id="guest_phone"
                      name="guest_phone"
                      data-testid="booking-phone-input"
                      value={formData.guest_phone}
                      onChange={handleChange}
                      placeholder="+234 XXX XXX XXXX"
                      className="w-full border border-[#E5E5E5] px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#C9A84C] transition-colors"
                    />
                    {errors.guest_phone && <p className="text-red-600 text-sm mt-1">{errors.guest_phone}</p>}
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">
                        <Calendar size={14} className="inline mr-2" />
                        Check-in *
                      </label>
                      <DatePicker
                        selected={formData.check_in}
                        onChange={(date) => {
                          setFormData({ ...formData, check_in: date });
                          if (errors.check_in) setErrors({ ...errors, check_in: null });
                        }}
                        minDate={new Date()}
                        dateFormat="dd/MM/yyyy"
                        data-testid="booking-checkin-input"
                        className="w-full border border-[#E5E5E5] px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#C9A84C] transition-colors"
                        placeholderText="Select date"
                      />
                      {errors.check_in && <p className="text-red-600 text-sm mt-1">{errors.check_in}</p>}
                    </div>

                    <div>
                      <label className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">
                        <Calendar size={14} className="inline mr-2" />
                        Check-out *
                      </label>
                      <DatePicker
                        selected={formData.check_out}
                        onChange={(date) => {
                          setFormData({ ...formData, check_out: date });
                          if (errors.check_out) setErrors({ ...errors, check_out: null });
                        }}
                        minDate={formData.check_in || new Date()}
                        dateFormat="dd/MM/yyyy"
                        data-testid="booking-checkout-input"
                        className="w-full border border-[#E5E5E5] px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#C9A84C] transition-colors"
                        placeholderText="Select date"
                      />
                      {errors.check_out && <p className="text-red-600 text-sm mt-1">{errors.check_out}</p>}
                    </div>
                  </div>

                  {/* Number of Guests */}
                  <div>
                    <label htmlFor="num_guests" className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">
                      <Users size={14} className="inline mr-2" />
                      Number of Guests *
                    </label>
                    <select
                      id="num_guests"
                      name="num_guests"
                      data-testid="booking-guests-select"
                      value={formData.num_guests}
                      onChange={handleChange}
                      className="w-full border border-[#E5E5E5] px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#C9A84C] transition-colors"
                    >
                      {[1, 2, 3, 4].map((num) => (
                        <option key={num} value={num}>
                          {num} {num === 1 ? 'Guest' : 'Guests'}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Special Requests */}
                  <div>
                    <label htmlFor="special_requests" className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">
                      Special Requests (Optional)
                    </label>
                    <textarea
                      id="special_requests"
                      name="special_requests"
                      data-testid="booking-requests-input"
                      value={formData.special_requests}
                      onChange={handleChange}
                      rows="4"
                      className="w-full border border-[#E5E5E5] px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#C9A84C] transition-colors resize-none"
                      placeholder="Any special requirements or preferences?"
                    />
                  </div>
                </div>
              </motion.form>
            </div>

            {/* Booking Summary */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                data-testid="booking-summary"
                className="bg-[#1A1A1A] text-[#FAF8F5] p-8 sticky top-24"
              >
                <h3 className="font-serif text-2xl tracking-tight font-light mb-6">Booking Summary</h3>

                {selectedSuite && (
                  <>
                    <div className="mb-6">
                      <img
                        src={selectedSuite.image_url}
                        alt={selectedSuite.name}
                        data-testid="summary-suite-image"
                        className="w-full h-48 object-cover mb-4"
                      />
                      <h4 className="text-lg font-medium mb-2" data-testid="summary-suite-name">{selectedSuite.name}</h4>
                      <p className="text-sm text-[#FAF8F5]/70" data-testid="summary-suite-price">
                        ₦{selectedSuite.price_per_night.toLocaleString()} per night
                      </p>
                    </div>

                    <div className="space-y-3 border-t border-[#FAF8F5]/20 pt-6">
                      {formData.check_in && formData.check_out && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span>Check-in:</span>
                            <span data-testid="summary-checkin">{formData.check_in.toLocaleDateString()}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Check-out:</span>
                            <span data-testid="summary-checkout">{formData.check_out.toLocaleDateString()}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Number of nights:</span>
                            <span data-testid="summary-nights">{calculateNumNights()}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between text-sm">
                        <span>Guests:</span>
                        <span data-testid="summary-guests">{formData.num_guests}</span>
                      </div>
                    </div>

                    <div className="border-t border-[#FAF8F5]/20 mt-6 pt-6">
                      <div className="flex justify-between items-center mb-6">
                        <span className="text-lg font-medium">Total Amount:</span>
                        <span className="text-2xl font-serif text-[#C9A84C]" data-testid="summary-total">
                          ₦{calculateTotalAmount().toLocaleString()}
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={handleSubmit}
                        data-testid="booking-submit-btn"
                        disabled={isSubmitting}
                        className="w-full bg-[#C9A84C] text-[#1A1A1A] hover:bg-[#FAF8F5] transition-colors duration-300 px-8 py-4 text-sm tracking-widest uppercase disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {isSubmitting ? 'Processing...' : 'Proceed to Payment'}
                        <CreditCard size={16} />
                      </button>

                      <p className="text-xs text-[#FAF8F5]/60 text-center mt-4">
                        Secure payment powered by Paystack
                      </p>
                    </div>
                  </>
                )}
              </motion.div>
            </div>
          </div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-12 text-center"
          >
            <p className="text-[#3A3A3A] mb-4">
              Need help with your booking? Call us directly:
            </p>
            <a
              href="tel:+2348066722221"
              data-testid="booking-phone-link"
              className="text-[#C9A84C] hover:text-[#1A1A1A] transition-colors text-lg font-medium"
            >
              +234 806 672 2221
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Booking;
